import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { VStack } from '~/components/elements/VStack'
import { useRestoreOnMount } from '~/hooks/useRestoreOnMount'
import { useScrollEffect } from '~/hooks/useScrollEffect'
import { exists } from '~/utils'

import { isTabulationReadyAtom, tabulationQueryDataAtom } from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'

import styles from './TimeSeriesAnalysis.css'
import { TimeSeriesAnalysisBody } from './TimeSeriesAnalysisBody'
import { TimeSeriesAnalysisHeader } from './TimeSeriesAnalysisHeader'
import { TimeSeriesAnalysisNavigation } from './TimeSeriesAnalysisNavigation'

export const TimeSeriesAnalysis = () => {
  const brandAccountId = useBrandAccountId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('timeSeriesReport'),
      createBreadcrumb('timeSeriesAnalysis', { brandAccountId }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const isExecuting = useTabulationQuery()

  const restoreParams = useRestoreParams()
  useRestoreOnMount(restoreParams, isTabulationReadyAtom)

  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  useScrollEffect(exists(tabulationQueryData))

  return (
    <PageContainer
      breadcrumbs={breadcrumbs}
      executing={isExecuting}
      navigation={<TimeSeriesAnalysisNavigation />}
      noPadSections={['content']}
    >
      <VStack className={styles.root} spacing={0}>
        <TimeSeriesAnalysisHeader className={styles.header} />

        <TimeSeriesAnalysisBody className={styles.content} />
      </VStack>
    </PageContainer>
  )
}
